import { Trans } from '@lingui/macro';
import { Download, Pin } from 'lucide-react';
import { useEffect, useState } from 'react';
import { RoomItem } from '../../models/RoomItem';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import { FileIconThumb } from './MessageFileItem';
import { MouseOverPreview } from './ItemFilePopover';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Progress } from "../../components/ui/progress";
import { cn } from "../../lib/utils";

interface IAddFromMyRoomFilesModal { onCancel: () => void, onDone: (selectedItems: RoomItem[]) => void };

export const AddFromMyRoomFilesModal: React.FC<IAddFromMyRoomFilesModal> = ({ onCancel, onDone }) => {
  const [selectedItems, setSelectedItems] = useState<RoomItem[]>([]);
  const [myRoomItems, setMyRoomItems] = useState<RoomItem[]>([]);
  const [inProgress, setInProgress] = useState(false);

  function close() {
    onDone(selectedItems);
  }

  function toggleSelection(item: RoomItem) {
    if (selectedItems.find(i => i.id === item.id)) {
      setSelectedItems(selectedItems.filter(i => i.id !== item.id));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  }

  useEffect(() => {
    setInProgress(true);
    return Fire.item.subscribeToRoomItemsByType(My.user.id, ['file'], items => {
      setMyRoomItems(items);
      setInProgress(false);
    });
  }, [])

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-3xl h-[80vh] flex flex-col p-0 z-[2000] gap-0">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle><Trans>Select files from My Room</Trans></DialogTitle>
        </DialogHeader>

        {inProgress && <Progress value={undefined} className="h-0.5 w-full rounded-none" />}

        <div className="flex-1 overflow-hidden relative">
          <div className="h-full overflow-y-auto">
            <div className="p-6 pb-20 space-y-2">
              {myRoomItems.map(item => (
                <FileItemRow
                  key={item.id}
                  item={item}
                  isSelected={!!selectedItems.find(i => i.id === item.id)}
                  onToggle={() => toggleSelection(item)}
                />
              ))}
              {!inProgress && myRoomItems.length == 0 && (
                <p className="text-center text-gray-500 mt-10"><Trans>You have no files in My Room</Trans></p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-gray-50 shrink-0">
          <Button variant="outline" onClick={onCancel} className="mr-auto"><Trans>Cancel</Trans></Button>
          <Button onClick={close} disabled={selectedItems.length === 0}>
            <Trans>Done</Trans> {selectedItems.length > 0 && `(${selectedItems.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const FileItemRow: React.FC<{ item: RoomItem, isSelected: boolean, onToggle: () => void }> = ({ item, isSelected, onToggle }) => {
  const itemTitle = () => (
    <div className="flex items-center gap-2">
      <div className="shrink-0 scale-75 origin-left">
        <FileIconThumb filename={item.title ?? ''} url={item.url ?? ''} />
      </div>
      <a
        href={item.url}
        target={'_blank'}
        onClick={(e) => e.stopPropagation()}
        className="text-sm font-medium hover:underline truncate max-w-[300px] text-gray-900"
      >
        {item.title ?? item.url}
      </a>
      {item.pinned && <Pin className="w-3 h-3 text-gray-400 rotate-45" />}
    </div>
  );

  return (
    <div
      className={cn(
        "flex items-center gap-4 p-3 rounded-lg border transition-all cursor-pointer hover:bg-gray-50",
        isSelected ? "border-orange-500 bg-orange-50/10" : "border-gray-100 bg-white"
      )}
      onClick={onToggle}
    >
      <Checkbox checked={isSelected} onCheckedChange={onToggle} />

      <div className="flex-1 min-w-0">
        <MouseOverPreview imageUrl={item.url} content={itemTitle} />
      </div>

      <div className="w-32 text-sm text-gray-500 truncate hidden md:block">
        {item.user.name}
      </div>

      {/* Downloads count if needed (currently mocked as 0 or need standard props, assuming user didnt ask for downloads logic here specifically but preserving visuals if easy. 
                Original passed 'downloads={[]}' so it was likely hidden. Omitting.) 
            */}
    </div>
  )
}
